import React, { useCallback, useEffect, useState } from "react";
import { Loader2 } from "lucide-react";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/AuthContext";
import { useLanguage, useT } from "@/lib/LanguageContext";
import { useTenant } from "@/lib/TenantContext";
import { isTenantHost } from "@/lib/tenantHost";
import { tenantHref } from "@/lib/tenantPath";
import { beginBridgeSignin } from "@/lib/authBridge";
import GoogleLoginButton from "@/lib/GoogleLoginButton";
import { InputOTP, InputOTPGroup, InputOTPSlot } from "@/components/ui/input-otp";
import { CLIENT_AUTH } from "@/constants/testIds/auth";
// Relative, not "@/...": real module under Jest, no alias there — the canonical email
// validator (lib/validation.js), replacing the local EMAIL_RE regex below.
import { isValidEmail } from "../../lib/validation";
import FieldError from "../ui/FieldError";
import { resolveBrandName } from "../../lib/siteLinks";

/**
 * otpErrorKey — pure mapping from a backend OTP error code to the clientAuth.*
 * i18n key that explains it (docs/auth-and-admin/client-otp-auth-contract.md).
 * Kept as a standalone export so the table is independently testable, separate
 * from the literal `t()` calls below that the keyCoverage static scan needs to
 * see (a computed `t(otpErrorKey(x))` call would be invisible to that scanner).
 */
export function otpErrorKey(detail) {
  switch (detail) {
    case "OTP_INVALID": return "clientAuth.errInvalid";
    case "OTP_EXPIRED": return "clientAuth.errExpired";
    case "OTP_TOO_MANY_ATTEMPTS": return "clientAuth.errTooMany";
    case "OTP_RESEND_COOLDOWN": return "clientAuth.errCooldown";
    case "RATE_LIMITED": return "clientAuth.errRateLimited";
    // 502: the provider REFUSED the message, so no code was ever sent and the backend
    // deleted the one it had just minted (BUG-043). Distinct from errGeneric because the
    // action it asks for is specific — check the address, then try again — and because
    // telling somebody to "check their inbox" for an email that was refused is the exact
    // lie this error exists to stop.
    case "OTP_DELIVERY_FAILED": return "clientAuth.errDeliveryFailed";
    // 400: the code was CORRECT and the address is a platform administrator or sits on a
    // configured super-admin domain, so no client session may be minted for it (BUG-043
    // re-open). It used to answer OTP_INVALID, which told somebody holding the right code
    // that their code was wrong and said nothing about the one route that does work.
    case "OTP_PRIVILEGED_ACCOUNT": return "clientAuth.errPrivileged";
    // 400 after a CORRECT code: a client sign-in on a practice site must accept the terms
    // of use and the privacy policy. The panel never sends one unticked; this covers a
    // stale tab or a direct API call.
    case "TERMS_NOT_ACCEPTED": return "clientAuth.errTermsRequired";
    default: return "clientAuth.errGeneric";
  }
}

// One literal t() call per error key `otpErrorKey` can return, so the static
// keyCoverage scan (i18n/__tests__/keyCoverage.test.js) can verify each key
// actually exists in all three languages.
function errorText(t, key) {
  switch (key) {
    case "clientAuth.errInvalid": return t("clientAuth.errInvalid");
    case "clientAuth.errExpired": return t("clientAuth.errExpired");
    case "clientAuth.errTooMany": return t("clientAuth.errTooMany");
    case "clientAuth.errCooldown": return t("clientAuth.errCooldown");
    case "clientAuth.errRateLimited": return t("clientAuth.errRateLimited");
    case "clientAuth.errDeliveryFailed": return t("clientAuth.errDeliveryFailed");
    case "clientAuth.errPrivileged": return t("clientAuth.errPrivileged");
    case "clientAuth.errTermsRequired": return t("clientAuth.errTermsRequired");
    default: return t("clientAuth.errGeneric");
  }
}

// Same reason as errorText, for the FIELD-level key held in `emailErrorKey`: the state
// carries the key and the sentence is resolved at render (so a language switch after a
// refused submit re-renders in the new language), and the literal t() call keeps the key
// visible to the keyCoverage scan.
function fieldErrorText(t, key) {
  switch (key) {
    case "clientAuth.emailInvalid": return t("clientAuth.emailInvalid");
    case "clientAuth.errTermsRequired": return t("clientAuth.errTermsRequired");
    default: return "";
  }
}

// "I accept the {terms} and the {privacy}": the sentence comes from the locale layer
// whole (word order is the language's business), and the two placeholders become links
// to the documents the practice site serves. A new tab, so the sign-in in progress here
// is not lost by reading them.
function termsSentence(t) {
  const template = t("clientAuth.termsAccept");
  const links = {
    "{terms}": [tenantHref("/terms"), t("clientAuth.termsLink"), CLIENT_AUTH.termsLink],
    "{privacy}": [tenantHref("/legal/privacy"), t("clientAuth.privacyLink"), CLIENT_AUTH.privacyLink],
  };
  return template.split(/(\{terms\}|\{privacy\})/).map((part, i) => {
    const link = links[part];
    if (!link) return part;
    const [href, label, testId] = link;
    return (
      <a key={i} href={href} target="_blank" rel="noopener noreferrer" data-testid={testId}
        className="underline text-eh-sage-deep">{label}</a>
    );
  });
}

// "j***@example.com" — never the full local part, just enough for the visitor to
// recognise their own address.
function maskEmail(email) {
  const at = String(email || "").indexOf("@");
  if (at <= 0) return email || "";
  const user = email.slice(0, at);
  const domain = email.slice(at + 1);
  return `${user.charAt(0)}${"*".repeat(Math.max(user.length - 1, 1))}@${domain}`;
}

/**
 * ClientAuthPanel — shared body of the client sign-in UI: Google (GIS on the
 * platform host, the tenant-host bridge otherwise) + email/6-digit-OTP, which
 * works natively on any tenant host. State machine: method-choice + email-entry
 * are the SAME screen (both affordances are visible together — there is nothing
 * to type before choosing a method, so splitting them into two screens would
 * only cost a click) → code-entry once a code has been requested, with a "use a
 * different email" control on the code step to go back to the form (BUG-044).
 *
 * STYLING — the card is a SELF-CONTAINED LIGHT SURFACE (BUG-047). Every colour is one of
 * the `eh-*` tokens, which tailwind.config.js defines as literal hex: cream ground, ink
 * type, ink-on-cream buttons, sage-deep links. The panel therefore inherits NO colour from
 * whatever renders it, and it looks the same in both of its embeddings.
 *
 * It used to paint itself with `var(--tpl-*, <Classic fallback>)` so it would take the
 * tenant's t2–t6 theme. That was wrong the moment BUG-006 moved it inside
 * templates/shared/TemplateShell.jsx's `[data-tpl]` scope: on a DARK template the theme's
 * text colour is CREAM, and it was being painted onto this card's own cream ground —
 * heading, "or" divider, email label and both button labels went invisible on t4 Bold, and
 * the primary button drew `--tpl-primary` under `--tpl-bg`, dark on dark. A sign-in card is
 * not decoration that may degrade with the theme; it is the one surface that must be
 * readable, so its palette is its own and the theme's job stops at the chrome around it.
 *
 * @param {(user: {user_id:string,email:string,name:string,picture:string}) => void} onSuccess
 * @param {() => void} [onCancel]  renders a Cancel affordance when provided (the
 *   dialog wrapper passes one; the ClientPortal standalone embedding does not).
 * @param {(nonce: string) => void} [onBeforeGoogleBridge]  BUG-195 — handed straight to
 *   `beginBridgeSignin` as its `onNonceReady`: called the instant before the TENANT-host
 *   Google button navigates the whole page away, with the single-use nonce this bridge
 *   attempt just minted, never on the platform-host popup path or the OTP path (neither
 *   one leaves the page). Lets `useRequireAuth`'s caller persist state that would
 *   otherwise be lost when this component unmounts mid-sign-in, keyed to that nonce so it
 *   cannot be read back by anything that did not just go through this same handoff.
 * @param {string} [initialEmail]  the address the form starts with ("" by default): a
 *   caller that has just collected it asks for it to be prefilled (useRequireAuth's
 *   `prefill` option). Read once, when the panel mounts; the field stays editable.
 * @param {boolean} [clientConsents=true]  on a practice host, the two boxes a CLIENT
 *   answers before signing in (owner decision 2026-10-05), sent with both methods:
 *     - REQUIRED "I accept the terms of use and the privacy policy" — neither Send code
 *       nor Google proceeds unticked (and the server refuses `TERMS_NOT_ACCEPTED`);
 *     - OPTIONAL, unticked "Send me news and offers from {practice} by email" — the practice's
 *       marketing consent (what starts the Welcome sequence).
 *   The server records both on the practice's client (`modules/crm/signup.py`). The
 *   team-invite page passes false: accepting an invite is not becoming a client.
 */
export default function ClientAuthPanel({ onSuccess, onCancel, onBeforeGoogleBridge, initialEmail = "",
  clientConsents = true }) {
  const t = useT("public");
  const { lang } = useLanguage();
  const { refresh } = useAuth();
  const tenantHost = isTenantHost();
  const { tenant } = useTenant() || {};
  // Only a practice host has a practice to consent to; the platform host has none.
  const askConsent = tenantHost && clientConsents;
  const [marketingConsent, setMarketingConsent] = useState(false);
  const [termsAccepted, setTermsAccepted] = useState(false);
  // The required box's own judgment, shown under it — a key, resolved at render.
  const [termsErrorKey, setTermsErrorKey] = useState("");
  /** true when the sign-in may proceed; otherwise says why, under the box. */
  const termsOk = () => {
    if (!askConsent || termsAccepted) return true;
    setTermsErrorKey("clientAuth.errTermsRequired");
    return false;
  };

  const [step, setStep] = useState("form"); // "form" | "code"
  const [email, setEmail] = useState(initialEmail);
  const [code, setCode] = useState("");
  const [sending, setSending] = useState(false);
  const [verifying, setVerifying] = useState(false);
  const [errorKey, setErrorKey] = useState("");
  const [cooldown, setCooldown] = useState(0);
  // Field-level: the email step's OWN judgment of what was typed, distinct from
  // `errorKey` (a server-side OTP refusal, rendered by CLIENT_AUTH.error below). THE KEY,
  // not the sentence — same rule as `errorKey` and as lib/GoogleLoginButton.jsx: a
  // resolved string frozen into state keeps displaying the language the visitor had when
  // they made the mistake, even after they switch it.
  const [emailErrorKey, setEmailErrorKey] = useState("");

  useEffect(() => {
    if (cooldown <= 0) return undefined;
    const id = setInterval(() => setCooldown((s) => Math.max(0, s - 1)), 1000);
    return () => clearInterval(id);
  }, [cooldown]);

  const requestCode = useCallback(async (isResend) => {
    setErrorKey("");
    setSending(true);
    try {
      const { data } = await api.post("/auth/otp/request", {
        email: email.trim().toLowerCase(),
        locale: lang,
      });
      setCooldown(Number(data?.cooldown_s) || 60);
      setStep("code");
      if (!isResend) setCode("");
    } catch (e) {
      const detail = e?.response?.data?.detail;
      // A 422 IS ALWAYS ABOUT THE ADDRESS, and it is the one refusal whose `detail` is not
      // a code at all — FastAPI answers pydantic's issue LIST, so `otpErrorKey` fell to
      // "Something went wrong. Please try again.", which is neither true nor actionable
      // (BUG-043). The endpoint's body has exactly two fields: `locale`, which this panel
      // sets itself from the language context and can only ever be fr/en/es, and `email`.
      // So a 422 here means the server's own validator refused the address — which the
      // client-side check does let through, `.test` and other reserved TLDs being the
      // case observed — and the honest place to say so is under the field itself.
      if (e?.response?.status === 422) {
        setEmailErrorKey("clientAuth.emailInvalid");
        setCode("");
        setStep("form");
        return;
      }
      const key = otpErrorKey(typeof detail === "string" ? detail : "");
      setErrorKey(key);
      // NO STEP ADVANCE ON ANY FAILURE — `setStep("code")` lives in the try above, after
      // the request came back 200. That is the shape of BUG-043: "Code sent" must be said
      // only by a request that actually succeeded. The two branches below are the two
      // deliberate exceptions, and both move the visitor TOWARDS the screen where they can
      // act, never towards a code that does not exist.
      //
      // A refused DELIVERY (502) sends the visitor back to the email field wherever they
      // were: the backend deleted the code it had just minted, so any code on screen is
      // dead, and the thing to check is the address itself. The cooldown is not consumed by
      // a failed send, so the retry from here is immediate.
      if (detail === "OTP_DELIVERY_FAILED") {
        setCode("");
        setStep("form");
      }
      // A cooldown refusal means a code is already pending for this address — the
      // right next step is the code screen (with the resend button), not leaving
      // the visitor stranded on the email form.
      if (detail === "OTP_RESEND_COOLDOWN") {
        const retryAfter = Number(e?.response?.headers?.["retry-after"]);
        setCooldown(Number.isFinite(retryAfter) && retryAfter > 0 ? retryAfter : 60);
        setStep("code");
      }
    } finally {
      setSending(false);
    }
  }, [email, lang]);

  const submitEmail = (e) => {
    e.preventDefault();
    if (sending) return;
    // Content validity (a blank/malformed address) is judged HERE, on submit — the
    // button itself disables only for `sending` (house rule: never disable for content
    // validity; a dead button gives no reason a visitor could act on).
    // Both judgments at once, so one press says everything that is missing.
    const emailOk = isValidEmail(email.trim());
    setEmailErrorKey(emailOk ? "" : "clientAuth.emailInvalid");
    if (!termsOk() || !emailOk) return;
    requestCode(false);
  };

  const verify = async (e) => {
    e?.preventDefault();
    if (verifying || code.length !== 6) return;
    setErrorKey("");
    setVerifying(true);
    try {
      const { data } = await api.post("/auth/otp/verify", {
        email: email.trim().toLowerCase(),
        code,
        marketing_consent: askConsent && marketingConsent,
        terms_accepted: askConsent && termsAccepted,
        locale: lang,
      });
      await refresh();
      onSuccess?.(data);
    } catch (e2) {
      const detail = e2?.response?.data?.detail;
      setErrorKey(otpErrorKey(typeof detail === "string" ? detail : ""));
      setCode("");
      // A PRIVILEGED ADDRESS IS A TERMINAL REFUSAL, and the route that does work is the
      // Google button — which lives on the FORM step. Leaving the visitor on the code
      // step would point them at an affordance they cannot see and offer them a Resend
      // that can only refuse them again; the server has already consumed the code, so
      // there is nothing on this screen left to do.
      if (detail === "OTP_PRIVILEGED_ACCOUNT") setStep("form");
    } finally {
      setVerifying(false);
    }
  };

  // "Use a different email" (BUG-044): the code step otherwise has no way back to the
  // email field, so a mistyped address is a dead end — the only recourse was reloading
  // the page. Returns to the form step with the SAME email still in the input (so a
  // near-miss typo is a quick edit, not a re-type from scratch) and clears the code/error
  // left over from the abandoned attempt.
  //
  // `cooldown` is deliberately left untouched here. It is only ever set from a real
  // backend response — a fresh `cooldown_s` on a successful /auth/otp/request, or the
  // Retry-After header on a 429 OTP_RESEND_COOLDOWN — and it only ever gates the CODE
  // STEP's Resend button, never the form step's Send-code button (see its `disabled`
  // above: `sending` only — content validity is judged in submitEmail() on submit). So
  // a stale countdown here cannot block a different
  // address from being submitted; whichever address the visitor sends next overwrites
  // `cooldown` with the server's authoritative value for THAT address. If they come back
  // with the SAME address before it would have expired, the backend will say so again
  // (OTP_RESEND_COOLDOWN) and the existing catch branch re-derives the display from the
  // Retry-After header — never from what was left running locally.
  const changeEmail = () => {
    setCode("");
    setErrorKey("");
    setStep("form");
  };

  // GIS already posts the session and calls AuthContext.refresh() itself
  // (lib/GoogleLoginButton.jsx) before invoking onSuccess — but that refresh()
  // updates AuthContext asynchronously, and this callback's own closure has no
  // way to read the freshly-resolved user out of it. /auth/me is the same
  // authoritative source refresh() itself reads; asking it directly is the
  // robust way to hand the real user object to onSuccess (vs. guessing from a
  // possibly-stale closure).
  const handleGoogleSuccess = async () => {
    try {
      const { data } = await api.get("/auth/me");
      onSuccess?.(data);
    } catch {
      // Should not happen right after GIS sets the cookie; leave the panel open
      // rather than call onSuccess with no user.
    }
  };

  // The card's palette, declared once. Classes, not inline `style`: these are literal-hex
  // Tailwind tokens (tailwind.config.js `eh`), so nothing on the page can rebind them the
  // way a CSS variable can — which is exactly the failure mode BUG-047 was.
  const MUTED = "text-eh-muted";
  const PRIMARY_BTN = "bg-eh-ink text-eh-cream";
  const LINK = "text-eh-sage-deep";

  return (
    <div
      data-testid={CLIENT_AUTH.panel}
      className="w-full max-w-sm mx-auto text-center bg-eh-cream text-eh-ink rounded-2xl"
    >
      <h2 className="font-display text-2xl">{t("clientAuth.title")}</h2>
      <p className={`text-sm mt-1 ${MUTED}`}>{t("clientAuth.subtitle")}</p>

      {step === "form" ? (
        <div className="mt-6 space-y-4">
          {askConsent && (
            // ABOVE both methods: the Google button leaves the page the moment it is
            // pressed, so both boxes must already be answered by then. Neither is
            // pre-ticked: the required one is an active acceptance, the optional one a
            // separate, freely given marketing consent (GDPR art. 7(4), Loi 25).
            <div className="space-y-2 text-left">
              <label className="flex items-start gap-2 text-sm text-eh-ink cursor-pointer">
                <input
                  type="checkbox"
                  checked={termsAccepted}
                  onChange={(e) => { setTermsAccepted(e.target.checked); if (e.target.checked) setTermsErrorKey(""); }}
                  aria-invalid={termsErrorKey ? true : undefined}
                  aria-describedby={termsErrorKey ? "client-auth-terms-error" : undefined}
                  data-testid={CLIENT_AUTH.termsAccept}
                  className="mt-0.5 h-4 w-4 accent-eh-sage-deep"
                />
                <span>{termsSentence(t)}</span>
              </label>
              <div id="client-auth-terms-error">
                <FieldError message={fieldErrorText(t, termsErrorKey)} testId={CLIENT_AUTH.termsError} />
              </div>
              <label className="flex items-start gap-2 text-sm text-eh-ink cursor-pointer">
                <input
                  type="checkbox"
                  checked={marketingConsent}
                  onChange={(e) => setMarketingConsent(e.target.checked)}
                  data-testid={CLIENT_AUTH.marketingConsent}
                  className="mt-0.5 h-4 w-4 accent-eh-sage-deep"
                />
                <span>{t("clientAuth.marketingConsent", { practice: resolveBrandName(tenant, t("clientAuth.practiceFallback")) })}</span>
              </label>
            </div>
          )}
          <div data-testid={CLIENT_AUTH.googleButton}>
            {tenantHost ? (
              <button
                type="button"
                onClick={() => {
                  if (!termsOk()) return;
                  // BUG-195 — `onBeforeGoogleBridge`, if a caller registered one, fires
                  // from INSIDE `beginBridgeSignin` (via `onNonceReady`) — the instant the
                  // nonce for THIS attempt exists, still before the navigation that would
                  // unmount the caller. Passed the nonce, never called with none: a
                  // caller keying its own state to it (lib/pendingBookingDraft.js) must
                  // always have the real value, not a placeholder.
                  beginBridgeSignin(window.location.pathname + window.location.search, onBeforeGoogleBridge,
                    { marketingConsent: askConsent && marketingConsent,
                      termsAccepted: askConsent && termsAccepted });
                }}
                className={`w-full inline-flex items-center justify-center gap-2 px-6 py-3 rounded-full text-sm font-medium transition ${PRIMARY_BTN}`}
              >
                {t("clientAuth.google")}
              </button>
            ) : (
              <GoogleLoginButton onSuccess={handleGoogleSuccess} />
            )}
          </div>

          <div className="flex items-center gap-3">
            <span className="flex-1 h-px bg-eh-border-soft" />
            <span className={`text-xs uppercase tracking-wide ${MUTED}`}>{t("clientAuth.or")}</span>
            <span className="flex-1 h-px bg-eh-border-soft" />
          </div>

          <form onSubmit={submitEmail} className="space-y-3 text-left">
            <label htmlFor="client-auth-email" className={`text-xs uppercase tracking-wide block ${MUTED}`}>
              {t("clientAuth.emailLabel")}
            </label>
            <input
              id="client-auth-email"
              type="email"
              value={email}
              onChange={(e) => { setEmail(e.target.value); if (emailErrorKey) setEmailErrorKey(""); }}
              placeholder={t("clientAuth.emailPh")}
              data-testid={CLIENT_AUTH.emailInput}
              className="w-full bg-eh-surface border border-eh-border-soft rounded-xl px-3 py-2.5 text-sm text-eh-ink"
            />
            <FieldError message={fieldErrorText(t, emailErrorKey)} testId={CLIENT_AUTH.emailError} />
            <button
              type="submit"
              disabled={sending}
              data-testid={CLIENT_AUTH.sendCodeButton}
              className={`w-full inline-flex items-center justify-center gap-2 px-6 py-3 rounded-full text-sm font-medium disabled:opacity-60 transition ${PRIMARY_BTN}`}
            >
              {sending && <Loader2 className="animate-spin" size={14} />}
              {t("clientAuth.sendCode")}
            </button>
          </form>
        </div>
      ) : (
        <div className="mt-6 space-y-4">
          <p className={`text-sm ${MUTED}`}>
            {t("clientAuth.codeSentTo", { email: maskEmail(email) })}
          </p>
          <button
            type="button"
            onClick={changeEmail}
            data-testid={CLIENT_AUTH.changeEmailButton}
            className={`text-xs underline ${LINK}`}
          >
            {t("clientAuth.changeEmail")}
          </button>
          <form onSubmit={verify} className="flex flex-col items-center gap-4">
            <label className={`text-xs uppercase tracking-wide ${MUTED}`}>
              {t("clientAuth.codeLabel")}
            </label>
            <InputOTP maxLength={6} value={code} onChange={setCode} data-testid={CLIENT_AUTH.otpInput}>
              <InputOTPGroup>
                {[0, 1, 2, 3, 4, 5].map((i) => (
                  <InputOTPSlot key={i} index={i} />
                ))}
              </InputOTPGroup>
            </InputOTP>
            <button
              type="submit"
              disabled={verifying || code.length !== 6}
              data-testid={CLIENT_AUTH.verifyButton}
              className={`w-full inline-flex items-center justify-center gap-2 px-6 py-3 rounded-full text-sm font-medium disabled:opacity-60 transition ${PRIMARY_BTN}`}
            >
              {verifying && <Loader2 className="animate-spin" size={14} />}
              {t("clientAuth.verify")}
            </button>
          </form>
          <button
            type="button"
            onClick={() => requestCode(true)}
            disabled={cooldown > 0 || sending}
            data-testid={CLIENT_AUTH.resendButton}
            className={`text-xs underline disabled:no-underline disabled:opacity-50 ${LINK}`}
          >
            {cooldown > 0 ? t("clientAuth.resendIn", { s: cooldown }) : t("clientAuth.resend")}
          </button>
        </div>
      )}

      {errorKey && (
        <p className="mt-4 text-sm text-red-600" data-testid={CLIENT_AUTH.error}>
          {errorText(t, errorKey)}
        </p>
      )}

      {onCancel && (
        <button type="button" onClick={onCancel} className={`mt-6 text-xs underline ${MUTED}`}>
          {t("clientAuth.cancel")}
        </button>
      )}
    </div>
  );
}
